import { useNavigate } from "react-router-dom";
import { Building2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { triggerHaptic } from "@/hooks/useHaptic";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

interface Project {
  id: string;
  name: string;
  client: string;
  status: string;
  progress: number | null;
}

interface ProjectsCarouselProps {
  projects: Project[];
}

const statusConfig: Record<string, { label: string; color: string }> = {
  'em_andamento': { label: 'Em andamento', color: 'bg-blue-500' },
  'planejamento': { label: 'Planejamento', color: 'bg-muted-foreground' },
  'atrasado': { label: 'Atrasado', color: 'bg-destructive' },
  'concluido': { label: 'Concluído', color: 'bg-emerald-500' },
  'pausado': { label: 'Pausado', color: 'bg-amber-500' },
};

export function ProjectsCarousel({ projects }: ProjectsCarouselProps) {
  const navigate = useNavigate();

  if (projects.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projetos Recentes</h2>
        </div>
        <div className="bento-card p-6 text-center">
          <Building2 className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhum projeto ainda</p>
          <Button 
            size="sm" 
            className="btn-modern text-xs mt-3 h-auto px-4 py-2"
            onClick={() => navigate('/projects?create=true')}
          >
            Criar primeiro projeto
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projetos Recentes</h2>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 text-xs text-muted-foreground active:scale-[0.95] transition-transform"
          onClick={() => navigate('/projects')}
        >
          Ver todos
          <ChevronRight className="h-3 w-3 ml-0.5" />
        </Button>
      </div>
      
      <Carousel
        opts={{
          align: "start",
          loop: false,
        }}
        className="-mx-4"
      >
        <CarouselContent className="pl-4">
          {projects.map((project) => {
            const status = statusConfig[project.status] || { label: project.status, color: 'bg-muted-foreground' };
            
            return (
              <CarouselItem key={project.id} className="basis-[75%] pr-3">
                <div 
                  className="bento-card p-4 active:scale-[0.97] active:bg-accent/5 transition-all duration-200 ease-out cursor-pointer h-full"
                  onClick={() => {
                    triggerHaptic('light');
                    navigate(`/projetos/${project.id}`);
                  }}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-foreground truncate">{project.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{project.client}</p>
                    </div>
                    <span className={`text-[10px] px-2.5 py-1 rounded-full text-white font-medium shrink-0 ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${project.progress || 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-foreground font-bold">{project.progress || 0}%</span>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
